import React, { useState } from "react";
import {
  Box,
  Button,
  CssBaseline,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Divider,
  Card as MuiCard,
  Stack,
  Link,
  CircularProgress,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/system";
import { signUp } from "@aws-amplify/auth";
import StripyBackground from "./DiagonalCanvas";
import { useTenantConfig } from "../../contexts/TenantConfigContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { normalizeEmail } from "../../utils/email-utils";

interface SignUpProps {
  onSignUpSuccess: (email: string) => void;
  onSignUpFailure: (error: string) => void;
  onNavigateToSignIn: () => void;
}

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  overflowY: "auto",             // Enable vertical scrolling
  // Hide scrollbar for Webkit browsers
  "&::-webkit-scrollbar": {
    display: "none",
  },
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: "transparent", // Allow the gradient to show through
  // Hide scrollbar for Firefox
  scrollbarWidth: "none",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const SignUp: React.FC<SignUpProps> = ({ onSignUpSuccess, onSignUpFailure, onNavigateToSignIn }) => {
  const { config } = useTenantConfig();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState("");
  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password requirements: align with Cognito policy
  const passwordRequirementsRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{6,}$/;
  const passwordRequirementsText =
    "Password must be at least 6 characters long and include uppercase, lowercase, number, and special character.";

  const validateInputs = (): boolean => {
    let isValid = true;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password || !passwordRequirementsRegex.test(password)) {
      setPasswordError(true);
      setPasswordErrorMessage(passwordRequirementsText);
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      setConfirmPasswordErrorMessage("Passwords do not match.");
      isValid = false;
    } else {
      setConfirmPasswordError(false);
      setConfirmPasswordErrorMessage("");
    }

    return isValid;
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateInputs()) return;

    setLoading(true);

    // Normalize email to lowercase to prevent case sensitivity issues
    const normalizedEmail = normalizeEmail(email);

    try {
      await signUp({
        username: normalizedEmail,
        password,
        options: {
          userAttributes: {
            email: normalizedEmail,
            given_name: givenName,
            family_name: familyName,
          },
        },
      });
      onSignUpSuccess(normalizedEmail);
    } catch (error: any) {
      console.error("Sign-up error:", error);
      
      if (error.name === 'UsernameExistsException') {
        // User already exists - likely unconfirmed account
        setGeneralError(
          "An account with this email already exists. If you haven't verified your account yet, please check your email for the verification code."
        );
        // Pass additional context to parent component
        onSignUpFailure({
          ...error,
          message: "Account exists but may need verification",
          email: email,
          showVerificationOption: true
        });
      } else if (error.name === "InvalidPasswordException") {
        setGeneralError(passwordRequirementsText);
        onSignUpFailure(passwordRequirementsText);
      } else {
        setGeneralError(error.message || "Failed to sign up.");
        onSignUpFailure(error.message || "Failed to sign up.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <StripyBackground />
      <CssBaseline />
      <Box
        sx={{
          position: "absolute",
          top: "1rem",
          left: "1rem",
          zIndex: 2,
          color: "#fff",
          fontWeight: "bold",
          fontSize: "1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}
      >
        <img src={config?.branding.logoUrl || "/brand/app-icon.svg"} alt={`${config?.branding.displayName || 'RAF'} Logo`} height="32" style={{ width: 'auto', borderRadius: '4px' }} />
        {config?.branding.displayName || 'RAF'}
      </Box>
      <SignUpContainer>
        <Card>
          <Typography component="h1" variant="h4" sx={{ width: "100%", textAlign: "center" }}>
            Sign Up
          </Typography>
          <Divider sx={{ mt: '5px', mb: 2 }} />
          <Box
            component="form"
            onSubmit={handleSignUp}
            noValidate
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {generalError && (
              <Typography color="error" variant="body2">
                {generalError}
              </Typography>
            )}
            <FormControl>
              <FormLabel>Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                fullWidth
              />
            </FormControl>
            <FormControl>
              <FormLabel>First name</FormLabel>
              <TextField
                id="givenName"
                type="text"
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
                placeholder="John"
                fullWidth
              />
            </FormControl>
            <FormControl>
              <FormLabel>Last Name</FormLabel>
              <TextField
                id="familyName"
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="Doe"
                fullWidth
              />
            </FormControl>
            <FormControl>
              <FormLabel>Password</FormLabel>
              <Tooltip
                title={passwordRequirementsText}
                arrow
              >
                <TextField
                  error={passwordError}
                  helperText={passwordErrorMessage}
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((prev) => !prev)}
                          onMouseDown={(event) => event.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
              {!passwordError && (
                <Typography variant="caption" color="text.secondary">
                  {passwordRequirementsText}
                </Typography>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Confirm Password</FormLabel>
              <TextField
                error={confirmPasswordError}
                helperText={confirmPasswordErrorMessage}
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                backgroundColor: "hsl(var(--accent))",
                "&:hover": {
                  backgroundColor: "hsl(var(--accent) / 0.9)",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress color="inherit" size={20} sx={{ marginRight: 1 }} />
                  Signing up...
                </>
              ) : (
                "Sign Up"
              )}
            </Button>
          </Box>
          <Divider sx={{ my: 2 }}>or</Divider>
          <Typography variant="body2" sx={{ textAlign: "left" }}>
            Already have an account?{" "}
            <Link
              onClick={onNavigateToSignIn}
              component="button"
              variant="body2"
              sx={{
                textTransform: "none",
                color: "hsl(var(--accent))",
                verticalAlign: "baseline",
                display: "inline",
                padding: 0,
                minHeight: "auto",
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "hsl(var(--accent) / 0.85)",
                  textDecoration: "none",
                },
              }}
            >
              Sign In
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "left", mt: 0 }}>
            Need to verify your account?{" "}
            <Link
              onClick={() => {
                const emailParam = email ? `?email=${encodeURIComponent(email)}` : '';
                window.location.href = `/confirm-signup${emailParam}`;
              }}
              component="button"
              variant="body2"
              sx={{
                textTransform: "none",
                color: "hsl(var(--accent))",
                verticalAlign: "baseline",
                display: "inline",
                padding: 0,
                minHeight: "auto",
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "hsl(var(--accent) / 0.85)",
                  textDecoration: "none",
                },
              }}
            >
              Complete Verification
            </Link>
          </Typography>
        </Card>
      </SignUpContainer>
    </>
  );
};

export default SignUp;
